import { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Search, ChevronDown, ChevronRight, Calendar, Building2 } from 'lucide-react';
import { useProjects, Project, ScopingStage } from '@/hooks/useProjects';
import { useCustomers } from '@/hooks/useCustomers';
import { useOpportunities, useCreateProjectUnderOpportunity } from '@/hooks/useOpportunities';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { MobileFilterDrawer, MobileFilterButton, FilterField } from '@/components/common/MobileFilterDrawer';
import { cn } from '@/lib/utils';

// Scoping stages configuration (matches workflow)
const SCOPING_STAGES: Array<{
  id: ScopingStage;
  label: string;
  color: string;
  borderColor: string;
}> = [
  { id: 'partner_briefing', label: 'Partner Briefing', color: 'bg-gray-100 text-gray-700', borderColor: 'border-gray-400' },
  { id: 'services_positioned', label: 'Services Positioned', color: 'bg-blue-100 text-blue-700', borderColor: 'border-blue-400' },
  { id: 'scoping_workshop', label: 'Scoping Workshop', color: 'bg-yellow-100 text-yellow-700', borderColor: 'border-yellow-400' },
  { id: 'customer_review', label: 'Customer Review', color: 'bg-orange-100 text-orange-700', borderColor: 'border-orange-400' },
  { id: 'customer_approval', label: 'Customer Approval', color: 'bg-purple-100 text-purple-700', borderColor: 'border-purple-400' },
  { id: 'po_sent', label: 'PO Sent', color: 'bg-green-100 text-green-700', borderColor: 'border-green-400' },
];

export function Projects() {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [hasInitializedCollapse, setHasInitializedCollapse] = useState(false);

  const { data: customersData } = useCustomers({ limit: 100 });
  const { data, isLoading, error } = useProjects({ limit: 200 });

  const customers = customersData?.customers || [];

  const activeFilterCount = customerFilter ? 1 : 0;

  const resetFilters = () => {
    setCustomerFilter('');
  };

  // Filter and group projects by scoping stage
  const groupedProjects = useMemo(() => {
    const projects = data?.projects || [];

    // Apply filters
    const filtered = projects.filter((p) => {
      const matchesSearch =
        !searchQuery ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.customerName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCustomer = !customerFilter || p.customerId === customerFilter;
      return matchesSearch && matchesCustomer;
    });

    // Group by scoping stage
    const grouped = new Map<ScopingStage, Project[]>();
    SCOPING_STAGES.forEach((s) => grouped.set(s.id, []));

    filtered.forEach((project) => {
      const stage = project.currentScopingStage || 'partner_briefing';
      const group = grouped.get(stage);
      if (group) {
        group.push(project);
      }
    });

    return grouped;
  }, [data?.projects, searchQuery, customerFilter]);

  // Auto-collapse empty stages on initial load
  useEffect(() => {
    if (!hasInitializedCollapse && groupedProjects.size > 0) {
      const emptyStages = new Set<string>();
      groupedProjects.forEach((projects, stageId) => {
        if (projects.length === 0) {
          emptyStages.add(stageId);
        }
      });
      setCollapsedSections(emptyStages);
      setHasInitializedCollapse(true);
    }
  }, [groupedProjects, hasInitializedCollapse]);

  const toggleSection = (status: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Failed to load projects. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-xtravirt-dark">Projects</h1>
          <p className="text-sm text-gray-600 mt-1">
            {data?.total || 0} projects
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">New Project</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search - always visible */}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>

        {/* Mobile filter button */}
        <MobileFilterButton
          onClick={() => setShowFilterDrawer(true)}
          hasActiveFilters={activeFilterCount > 0}
          activeCount={activeFilterCount}
        />

        {/* Desktop: Customer Filter */}
        <div className="hidden md:flex items-center gap-2">
          <span className="text-sm text-gray-500">CUSTOMER</span>
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="input w-48"
          >
            <option value="">All Customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {SCOPING_STAGES.map((stageConfig) => {
          const projects = groupedProjects.get(stageConfig.id) || [];
          const isCollapsed = collapsedSections.has(stageConfig.id);

          return (
            <StageSection
              key={stageConfig.id}
              stageConfig={stageConfig}
              projects={projects}
              isCollapsed={isCollapsed}
              onToggle={() => toggleSection(stageConfig.id)}
              onProjectClick={(id) => navigate(`/projects/${id}`)}
            />
          );
        })}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateProjectModal onClose={() => setShowCreateModal(false)} />
      )}

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onReset={resetFilters}
        hasActiveFilters={activeFilterCount > 0}
      >
        <FilterField label="Customer">
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="input w-full"
          >
            <option value="">All Customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FilterField>
      </MobileFilterDrawer>
    </div>
  );
}

// Stage Section Component (for list view)
function StageSection({
  stageConfig,
  projects,
  isCollapsed,
  onToggle,
  onProjectClick,
}: {
  stageConfig: (typeof SCOPING_STAGES)[0];
  projects: Project[];
  isCollapsed: boolean;
  onToggle: () => void;
  onProjectClick: (id: string) => void;
}) {
  return (
    <div className="card overflow-hidden !p-0">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors',
          'border-l-4',
          stageConfig.borderColor
        )}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
        <span className="font-medium text-gray-900">{stageConfig.label}</span>
        <span
          className={cn(
            'inline-flex items-center justify-center min-w-[24px] px-2 py-0.5 rounded-full text-xs font-medium',
            stageConfig.color
          )}
        >
          {projects.length}
        </span>
      </button>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="border-t border-gray-100">
          {projects.length > 0 ? (
            <>
              {/* Mobile: Card view */}
              <div className="md:hidden p-3 space-y-2">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => onProjectClick(project.id)}
                    className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-xtravirt-dark text-sm block truncate">
                          {project.name}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <Building2 className="h-3 w-3" />
                          {project.customerName}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">v{project.version}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className="text-gray-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatRelativeDate(project.updatedAt)}
                      </span>
                      <span className="font-medium text-xtravirt-dark">
                        {project.totalAdjustedDays
                          ? `${(project.totalAdjustedDays / 100).toFixed(1)} days`
                          : '-'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table view */}
              <table className="hidden md:table min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Project
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Customer
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Days
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Version
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {projects.map((project) => (
                    <tr
                      key={project.id}
                      onClick={() => onProjectClick(project.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-medium text-xtravirt-dark hover:text-xtravirt-cyan">
                          {project.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {project.customerName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {project.totalAdjustedDays
                          ? (project.totalAdjustedDays / 100).toFixed(1)
                          : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        v{project.version}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {formatRelativeDate(project.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="px-4 py-8 text-center text-gray-500 text-sm italic">
              No projects in this stage
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Create Project Modal
function CreateProjectModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const createProject = useCreateProjectUnderOpportunity();
  const { data: opportunitiesData, isLoading: loadingOpportunities } = useOpportunities({
    status: 'pipeline',
    limit: 100,
  });
  const [opportunityError, setOpportunityError] = useState<string | undefined>();
  const [formData, setFormData] = useState({
    name: '',
    opportunityId: '',
    description: '',
  });

  const opportunities = opportunitiesData?.opportunities || [];
  const selectedOpportunity = opportunities.find((o) => o.id === formData.opportunityId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.opportunityId) {
      setOpportunityError('Please select an opportunity. Projects must belong to an opportunity.');
      return;
    }
    setOpportunityError(undefined);

    try {
      const result = await createProject.mutateAsync({
        opportunityId: formData.opportunityId,
        name: formData.name,
        description: formData.description || undefined,
      });
      navigate(`/projects/${result.project.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <div className="dialog-header">
          <h2 className="dialog-title">Create New Project</h2>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Opportunity *</label>
            <p className="text-xs text-gray-500 mb-2">
              Projects must belong to an opportunity.{' '}
              <Link to="/opportunities" className="text-primary-600 hover:underline">
                Create opportunity first
              </Link>
            </p>
            {loadingOpportunities ? (
              <div className="py-2">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <select
                value={formData.opportunityId}
                onChange={(e) => {
                  setFormData({ ...formData, opportunityId: e.target.value });
                  if (e.target.value) {
                    setOpportunityError(undefined);
                  }
                }}
                className={cn(
                  'input mt-1 w-full',
                  opportunityError && 'border-red-500 focus:ring-red-500'
                )}
              >
                <option value="">Select an opportunity...</option>
                {opportunities.map((opp) => (
                  <option key={opp.id} value={opp.id}>
                    {opp.customerName} - {opp.title}
                  </option>
                ))}
              </select>
            )}
            {opportunityError && (
              <p className="mt-1 text-xs text-red-500">{opportunityError}</p>
            )}
            {selectedOpportunity && (
              <p className="mt-1 text-xs text-gray-500">
                Customer: {selectedOpportunity.customerName}
              </p>
            )}
          </div>
          <div>
            <label className="label">Project Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input mt-1 w-full"
              placeholder="e.g., VCF Platform Buildout"
            />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="input mt-1 w-full"
              rows={3}
              placeholder="Brief project description..."
            />
          </div>
        </form>

        <div className="dialog-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={createProject.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary"
            disabled={!formData.opportunityId || !formData.name || createProject.isPending}
          >
            {createProject.isPending ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper function for relative dates
function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString('en-GB', { weekday: 'short' });
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
